import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "../client";
import type {
  Subscription,
  Issue,
  CreateSubscriptionInput,
  UpdateSubscriptionInput,
  CreateIssueInput,
  UpdateIssueInput,
  IssueActionResult,
  SubscriptionStatistics,
} from "../types";

const SUBSCRIPTIONS_KEY = "subscriptions";
const ISSUES_KEY = "issues";

// ===== SUBSCRIPTIONS =====

/**
 * Search subscriptions.
 */
export function useSubscriptionsSearch(query?: string, status?: string) {
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (status) params.set("status", status);
  const queryString = params.toString();

  return useQuery({
    queryKey: [SUBSCRIPTIONS_KEY, "search", query, status],
    queryFn: async () => {
      const url = queryString
        ? `/periodical/subscriptions?${queryString}`
        : "/periodical/subscriptions";
      return get<Subscription[]>(url);
    },
  });
}

/**
 * Get a single subscription by ID.
 */
export function useSubscription(id: string | undefined) {
  return useQuery({
    queryKey: [SUBSCRIPTIONS_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<Subscription>(`/periodical/subscriptions/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Create a new subscription.
 */
export function useCreateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateSubscriptionInput) => {
      return post<Subscription>("/periodical/subscriptions", input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS_KEY] });
    },
  });
}

/**
 * Update a subscription.
 */
export function useUpdateSubscription(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateSubscriptionInput) => {
      return patch<Subscription>(`/periodical/subscriptions/${id}`, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS_KEY] });
    },
  });
}

/**
 * Delete a subscription.
 */
export function useDeleteSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await del(`/periodical/subscriptions/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS_KEY] });
    },
  });
}

/**
 * Get subscriptions due for renewal.
 */
export function useSubscriptionsDueForRenewal() {
  return useQuery({
    queryKey: [SUBSCRIPTIONS_KEY, "due-renewal"],
    queryFn: async () => {
      return get<{
        count: number;
        subscriptions: Array<{
          id: string;
          title: string;
          endDate: string | null;
          autoRenew: boolean;
          annualCostInCents: number;
        }>;
      }>("/periodical/subscriptions/due-renewal");
    },
  });
}

/**
 * Renew a subscription.
 */
export function useRenewSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, endDate }: { id: string; endDate?: string }) => {
      return post<{ success: boolean; message: string; endDate: string }>(
        `/periodical/subscriptions/${id}/renew`,
        { endDate }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS_KEY] });
    },
  });
}

/**
 * Generate expected issues for a subscription.
 */
export function useGenerateIssues() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      subscriptionId,
      fromDate,
      toDate,
    }: {
      subscriptionId: string;
      fromDate?: string;
      toDate?: string;
    }) => {
      return post<{
        success: boolean;
        message: string;
        count: number;
        issues: Array<{
          id: string;
          displayLabel: string;
          expectedDate: string;
        }>;
      }>(`/periodical/subscriptions/${subscriptionId}/generate-issues`, {
        fromDate,
        toDate,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ISSUES_KEY] });
    },
  });
}

/**
 * Get subscription statistics.
 */
export function useSubscriptionStatistics(id: string | undefined) {
  return useQuery({
    queryKey: [SUBSCRIPTIONS_KEY, id, "statistics"],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<SubscriptionStatistics>(`/periodical/subscriptions/${id}/statistics`);
    },
    enabled: !!id,
  });
}

// ===== ISSUES =====

/**
 * Get issues for a subscription.
 */
export function useIssues(subscriptionId?: string, status?: string) {
  const params = new URLSearchParams();
  if (subscriptionId) params.set("subscription_id", subscriptionId);
  if (status) params.set("status", status);
  const queryString = params.toString();

  return useQuery({
    queryKey: [ISSUES_KEY, subscriptionId, status],
    queryFn: async () => {
      const url = queryString ? `/periodical/issues?${queryString}` : "/periodical/issues";
      return get<Issue[]>(url);
    },
  });
}

/**
 * Get a single issue by ID.
 */
export function useIssue(id: string | undefined) {
  return useQuery({
    queryKey: [ISSUES_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<Issue>(`/periodical/issues/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Create a new issue.
 */
export function useCreateIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateIssueInput) => {
      return post<Issue>("/periodical/issues", input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ISSUES_KEY] });
    },
  });
}

/**
 * Update an issue.
 */
export function useUpdateIssue(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateIssueInput) => {
      return patch<Issue>(`/periodical/issues/${id}`, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ISSUES_KEY] });
    },
  });
}

/**
 * Delete an issue.
 */
export function useDeleteIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await del(`/periodical/issues/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ISSUES_KEY] });
    },
  });
}

// ===== ISSUE ACTIONS =====

/**
 * Receive an issue.
 */
export function useReceiveIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      copiesReceived,
      receivedDate,
    }: {
      id: string;
      copiesReceived?: number;
      receivedDate?: string;
    }) => {
      return post<IssueActionResult>(`/periodical/issues/${id}/receive`, {
        copiesReceived,
        receivedDate,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ISSUES_KEY] });
      void queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS_KEY] });
    },
  });
}

/**
 * Mark an issue as late.
 */
export function useMarkIssueLate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return post<IssueActionResult>(`/periodical/issues/${id}/mark-late`, {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ISSUES_KEY] });
    },
  });
}

/**
 * Mark an issue as missing.
 */
export function useMarkIssueMissing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return post<IssueActionResult>(`/periodical/issues/${id}/mark-missing`, {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ISSUES_KEY] });
    },
  });
}

/**
 * Claim an issue.
 */
export function useClaimIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return post<IssueActionResult>(`/periodical/issues/${id}/claim`, {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ISSUES_KEY] });
    },
  });
}

/**
 * Mark an issue as not published.
 */
export function useMarkIssueNotPublished() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return post<IssueActionResult>(`/periodical/issues/${id}/mark-not-published`, {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ISSUES_KEY] });
    },
  });
}

/**
 * Get late issues.
 */
export function useLateIssues() {
  return useQuery({
    queryKey: [ISSUES_KEY, "late"],
    queryFn: async () => {
      return get<{
        count: number;
        issues: Array<{
          id: string;
          displayLabel: string;
          subscriptionTitle: string;
          expectedDate: string;
          status: string;
        }>;
      }>("/periodical/issues/late");
    },
  });
}

/**
 * Get expected issues.
 */
export function useExpectedIssues(daysAhead?: number) {
  const params = new URLSearchParams();
  if (daysAhead) params.set("days", String(daysAhead));
  const queryString = params.toString();

  return useQuery({
    queryKey: [ISSUES_KEY, "expected", daysAhead],
    queryFn: async () => {
      const url = queryString
        ? `/periodical/issues/expected?${queryString}`
        : "/periodical/issues/expected";
      return get<{
        count: number;
        daysAhead: number;
        issues: Array<{
          id: string;
          displayLabel: string;
          subscriptionTitle: string;
          expectedDate: string;
          copiesExpected: number;
        }>;
      }>(url);
    },
  });
}
